import {
  Component,
  createContext,
  createEffect,
  createSignal,
  JSX,
  useContext,
} from 'solid-js';
import {
  Environment,
  IssueManagementEnvironmentConfig,
  ISSUE_MANAGEMENT_ENV_CONFIG,
  DEFAULT_ENVIRONMENT as _DEFAULT_ENVIRONMENT,
  buildRegionalUrl,
} from './constants/environment-config';

interface ConfigurationContextValue {
  currentEnvironment(): Environment;
  config(): IssueManagementEnvironmentConfig;
  setEnvironment(environment: Environment): void;
  getIssueManagementDomain(): string;
  getTcpsApiDomain(): string;
  getUserApiDomain(): string;
  getFollowApiDomain(): string;
  getEcomApiDomain(): string;
  getBcfDomain(): string;
  getBatchApiDomain(): string;
  isIssueAclEnabled(): boolean;
  // Regional URL builders - accept region parameter
  getRegionalTcpsApiDomain(projectLocation?: string): string;
  getRegionalUserApiDomain(projectLocation?: string): string;
  getRegionalIssueManagementDomain(projectLocation?: string): string;
  getRegionalBcfDomain(projectLocation?: string): string;
}

const ConfigurationContext = createContext<ConfigurationContextValue>();

export interface ConfigurationProviderProps {
  children: JSX.Element;
  environment: Environment;
}

export const ConfigurationProvider: Component<ConfigurationProviderProps> = (
  props: ConfigurationProviderProps
) => {
  const [currentEnvironment, setCurrentEnvironment] =
    createSignal<Environment>();

  // Initialize and react to changes in props.environment
  createEffect(() => {
    setCurrentEnvironment(props.environment);
  });

  const config = (): IssueManagementEnvironmentConfig => {
    const env = currentEnvironment();
    if (!env) {
      throw new Error('Environment not initialized');
    }
    return ISSUE_MANAGEMENT_ENV_CONFIG[env];
  };

  const setEnvironment = (environment: Environment): void => {
    setCurrentEnvironment(environment);
  };

  const getIssueManagementDomain = (): string => {
    return config().issueManagementDomain;
  };

  const getTcpsApiDomain = (): string => {
    const domain = config().tcpsApiDomain;
    return domain;
  };

  const getUserApiDomain = (): string => {
    const domain = config().userApiDomain;
    return domain;
  };

  const getFollowApiDomain = (): string => {
    const domain = config().followApiDomain;
    return domain;
  };

  const getEcomApiDomain = (): string => {
    const domain = config().ecomApiDomain;
    return domain;
  };

  const getBcfDomain = (): string => {
    const domain = config().bcfDomain;
    return domain;
  };

  const getBatchApiDomain = (): string => {
    const domain = config().batchApiDomain;
    return domain;
  };

  const isIssueAclEnabled = (): boolean => {
    return config().enableIssueAcl;
  };

  // Regional URL builders
  const getRegionalTcpsApiDomain = (projectLocation?: string): string => {
    const env = currentEnvironment();
    if (!env) {
      throw new Error('Environment not initialized');
    }
    return buildRegionalUrl(env, projectLocation || null, 'tcps');
  };

  const getRegionalUserApiDomain = (projectLocation?: string): string => {
    const env = currentEnvironment();
    if (!env) {
      throw new Error('Environment not initialized');
    }
    return buildRegionalUrl(env, projectLocation || null, 'user');
  };

  const getRegionalIssueManagementDomain = (
    projectLocation?: string
  ): string => {
    const env = currentEnvironment();
    if (!env) {
      throw new Error('Environment not initialized');
    }
    return buildRegionalUrl(env, projectLocation || null, 'issue');
  };

  const getRegionalBcfDomain = (projectLocation?: string): string => {
    const env = currentEnvironment();
    if (!env) {
      throw new Error('Environment not initialized');
    }
    return buildRegionalUrl(env, projectLocation || null, 'bcf');
  };

  const contextValue: ConfigurationContextValue = {
    currentEnvironment: () => {
      const env = currentEnvironment();
      if (!env) {
        throw new Error('Environment not initialized');
      }
      return env;
    },
    config,
    setEnvironment,
    getIssueManagementDomain,
    getTcpsApiDomain,
    getUserApiDomain,
    getFollowApiDomain,
    getEcomApiDomain,
    getBcfDomain,
    getBatchApiDomain,
    isIssueAclEnabled,
    getRegionalTcpsApiDomain,
    getRegionalUserApiDomain,
    getRegionalIssueManagementDomain,
    getRegionalBcfDomain,
  };

  return (
    <ConfigurationContext.Provider value={contextValue}>
      {props.children}
    </ConfigurationContext.Provider>
  );
};

export const useConfiguration = (): ConfigurationContextValue => {
  const context = useContext(ConfigurationContext);
  if (context === undefined) {
    throw new Error(
      'useConfiguration must be used within a ConfigurationProvider'
    );
  }

  return context;
};
